// src/modules/units/tyres/shared/tyre-import-values.js
//
// Cell-value coercion for the tyre importer. Pure functions, no DOM, no I/O.
//
// A customer's spreadsheet is typed by whoever filled it in, so the same number
// arrives as 5035, "5035", "5 035", "R 5,035.00" or "5035,00", and the same date as
// an Excel serial, "2023-04-08", "08/04/2023" or "8 Apr 2023". Each parser takes
// whatever the cell holds and returns a result object, never throws:
//
//   { ok: true,  value }            — the coerced value (value is null for a blank)
//   { ok: false, error }            — a user-facing reason the cell was rejected
//
// Blank is a first-class outcome (ok + null), because an empty optional cell is not
// an error; the validator decides whether blank is allowed for the field.
//
// South African conventions decide the ambiguous cases, and are documented here so
// the rule is visible rather than accidental:
//   - dates are DAY-FIRST: 08/04/2023 is 8 April, never 4 August;
//   - a lone comma is a decimal separator ("19,8") UNLESS exactly three digits
//     follow it ("5,035"), which is read as a thousands separator.

export const isBlankCell = (v) =>
  v === null || v === undefined || (typeof v === 'string' && v.trim() === '');

// Plain text: trim, collapse internal runs of whitespace (including the NBSPs that
// copy-paste from web pages brings in). Numbers become their shortest string form,
// so an ID typed as 1001 in Excel still reads "1001".
export function cleanText(v) {
  if (isBlankCell(v)) return '';
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : '';
  if (typeof v === 'boolean') return v ? 'TRUE' : 'FALSE';
  return String(v).replace(/[\s   ]+/g, ' ').trim();
}

// ---------------------------------------------------------------------------
// Numbers
// ---------------------------------------------------------------------------

const CURRENCY_PREFIX = /^(?:zar|r|\$|usd|eur|€|£)\s*/i;
const UNIT_SUFFIX = /\s*(?:mm|km|kms|zar)\.?$/i;

/**
 * Parse a number from a cell.
 * @param {*} v the raw cell value
 * @returns {{ok:true,value:number|null}|{ok:false,error:string}}
 */
export function parseNumber(v) {
  if (isBlankCell(v)) return { ok: true, value: null };
  if (typeof v === 'number') {
    return Number.isFinite(v) ? { ok: true, value: v } : { ok: false, error: 'is not a number' };
  }
  if (typeof v === 'boolean') return { ok: false, error: 'is not a number' };

  let s = String(v).trim();
  let negative = false;

  // Accounting negatives: (123.45)
  if (/^\(.*\)$/.test(s)) { negative = true; s = s.slice(1, -1).trim(); }
  if (s.startsWith('-')) { negative = !negative; s = s.slice(1).trim(); }

  s = s.replace(CURRENCY_PREFIX, '').replace(UNIT_SUFFIX, '');
  if (s.startsWith('-')) { negative = !negative; s = s.slice(1); }
  // Grouping characters that are never decimal separators.
  s = s.replace(/[\s   ']/g, '');

  const commas = (s.match(/,/g) || []).length;
  const dots = (s.match(/\./g) || []).length;

  if (commas && dots) {
    // Both present: whichever comes LAST is the decimal separator.
    if (s.lastIndexOf(',') > s.lastIndexOf('.')) s = s.replace(/\./g, '').replace(',', '.');
    else s = s.replace(/,/g, '');
  } else if (commas > 1) {
    s = s.replace(/,/g, '');
  } else if (commas === 1) {
    // "5,035" groups thousands; "19,8" and "0,125" are decimals (a leading 0 can't
    // be a thousands group).
    const after = s.length - s.indexOf(',') - 1;
    const grouping = after === 3 && !/^0?,/.test(s);
    s = grouping ? s.replace(',', '') : s.replace(',', '.');
  } else if (dots > 1) {
    s = s.replace(/\./g, '');
  }

  if (!/^\d+(?:\.\d+)?$|^\.\d+$/.test(s)) return { ok: false, error: 'is not a number' };
  const n = Number(s);
  if (!Number.isFinite(n)) return { ok: false, error: 'is not a number' };
  return { ok: true, value: negative ? -n : n };
}

/** Round to `dp` decimal places without binary drift (19.8 stays 19.8). */
export function roundTo(n, dp) {
  const f = 10 ** dp;
  return Math.round((n + Number.EPSILON) * f) / f;
}

// ---------------------------------------------------------------------------
// Dates — always returned as a 'YYYY-MM-DD' string, the same shape the New Tyre
// form's <input type="date"> posts for purchaseDate.
// ---------------------------------------------------------------------------

const pad = (n) => String(n).padStart(2, '0');

function isRealDate(y, m, d) {
  if (!(y >= 1900 && y <= 2999 && m >= 1 && m <= 12 && d >= 1 && d <= 31)) return false;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

const ymd = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;

/**
 * Excel serial day number → 'YYYY-MM-DD'. The 1900 system counts from 1899-12-30
 * (which absorbs Lotus's phantom 29 Feb 1900 for every date after it); the 1904
 * system (old Mac workbooks) counts from 1904-01-01. Any time-of-day fraction is
 * dropped — a purchase date is a day.
 */
export function excelSerialToYmd(serial, { date1904 = false } = {}) {
  if (typeof serial !== 'number' || !Number.isFinite(serial) || serial < 1) return null;
  const epoch = date1904 ? Date.UTC(1904, 0, 1) : Date.UTC(1899, 11, 30);
  const dt = new Date(epoch + Math.floor(serial) * 86400000);
  const y = dt.getUTCFullYear();
  if (y < 1900 || y > 2999) return null;
  return ymd(y, dt.getUTCMonth() + 1, dt.getUTCDate());
}

const MONTHS = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, sept: 9, oct: 10, nov: 11, dec: 12,
  january: 1, february: 2, march: 3, april: 4, june: 6, july: 7, august: 8, september: 9,
  october: 10, november: 11, december: 12
};

/**
 * Parse a date from a cell.
 * Accepts: an Excel serial number; YYYY-MM-DD / YYYY/MM/DD (optionally with a time
 * part); DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY (day-first); "8 Apr 2023",
 * "08-Apr-2023", "April 8, 2023". Two-digit years are rejected rather than guessed.
 * @returns {{ok:true,value:string|null}|{ok:false,error:string}}
 */
export function parseDate(v, { date1904 = false } = {}) {
  if (isBlankCell(v)) return { ok: true, value: null };
  const bad = { ok: false, error: 'is not a recognised date (use YYYY-MM-DD or DD/MM/YYYY)' };

  if (typeof v === 'number') {
    const s = excelSerialToYmd(v, { date1904 });
    return s ? { ok: true, value: s } : bad;
  }
  if (v instanceof Date) {
    if (Number.isNaN(v.getTime())) return bad;
    return { ok: true, value: ymd(v.getFullYear(), v.getMonth() + 1, v.getDate()) };
  }

  const s = String(v).trim();
  let m;

  // A purely numeric string in the serial range: a date column exported as numbers.
  if (/^\d{5}(?:\.\d+)?$/.test(s)) {
    const out = excelSerialToYmd(Number(s), { date1904 });
    return out ? { ok: true, value: out } : bad;
  }

  // Year first: 2023-04-08, 2023/04/08, 2023-04-08T00:00:00Z
  if ((m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:[T\s].*)?$/))) {
    const [y, mo, d] = [+m[1], +m[2], +m[3]];
    return isRealDate(y, mo, d) ? { ok: true, value: ymd(y, mo, d) } : bad;
  }

  // Day first: 08/04/2023, 8-4-2023, 08.04.2023 (optional trailing time)
  if ((m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})(?:\s.*)?$/))) {
    if (m[3].length !== 4) return { ok: false, error: 'has a two-digit year — use a four-digit year' };
    const [d, mo, y] = [+m[1], +m[2], +m[3]];
    return isRealDate(y, mo, d) ? { ok: true, value: ymd(y, mo, d) } : bad;
  }

  // 8 Apr 2023, 08-Apr-2023, 8 April 2023
  if ((m = s.match(/^(\d{1,2})[\s\-/.]+([A-Za-z]{3,9})\.?[\s\-/.,]+(\d{4})$/))) {
    const mo = MONTHS[m[2].toLowerCase()];
    const [d, y] = [+m[1], +m[3]];
    return mo && isRealDate(y, mo, d) ? { ok: true, value: ymd(y, mo, d) } : bad;
  }

  // April 8, 2023 / Apr 8 2023
  if ((m = s.match(/^([A-Za-z]{3,9})\.?\s+(\d{1,2}),?\s+(\d{4})$/))) {
    const mo = MONTHS[m[1].toLowerCase()];
    const [d, y] = [+m[2], +m[3]];
    return mo && isRealDate(y, mo, d) ? { ok: true, value: ymd(y, mo, d) } : bad;
  }

  return bad;
}

// ---------------------------------------------------------------------------
// Vocabulary — case-insensitive match onto a canonical list.
// ---------------------------------------------------------------------------

const vocabKey = (s) => String(s).toLowerCase().replace(/[\s_\-/]+/g, ' ').trim();

/**
 * Match `text` onto `options` ignoring case and separator differences. Returns the
 * canonical spelling when found, else null (the caller keeps the original and
 * warns — an off-list value is saved as entered, exactly as the form allows).
 */
export function matchVocab(text, options = []) {
  if (!text) return null;
  const k = vocabKey(text);
  return options.find((o) => vocabKey(o) === k) || null;
}
